/** WhatsApp sticker limits. Static stickers cap at 100 KB; animated at 500 KB.
 * The client cannot tell them apart reliably, so it only blocks files over 500 KB.
 */
export const STICKER_MAX_BYTES = 500 * 1024;

const WEBP_MIME = 'image/webp';

export function fileNameExtension(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return null;
  return name.slice(dot + 1).toLowerCase();
}

/** True when the file or library resource can be sent as a WhatsApp sticker. */
export function isWhatsappStickerMedia(media: {
  name?: string;
  mime_type?: string;
  type?: string;
}): boolean {
  const mime = (media.mime_type || media.type || '').toLowerCase();
  const extension = fileNameExtension(media.name || '');
  if (mime === WEBP_MIME && (extension === null || extension === 'webp')) return true;
  if (extension === 'webp' && (mime === '' || mime === 'application/octet-stream')) return true;
  return false;
}

export type ComposerAttachmentKind = 'sticker' | 'image' | 'document' | 'audio';

/** Classifies a dropped or pasted file. On WhatsApp, WebP is sent as a sticker. */
export function classifyComposerFile(file: File, source?: string): ComposerAttachmentKind {
  if (file.type.startsWith('audio/')) return 'audio';
  if (source === 'whatsapp' && isWhatsappStickerMedia({ name: file.name, mime_type: file.type })) {
    return 'sticker';
  }
  if (file.type.startsWith('image/')) return 'image';
  return 'document';
}

export function acceptForAttachmentType(type: string, source?: string): string {
  if (type === 'sticker') return '.webp,image/webp';
  if (type === 'image') {
    if (source === 'instagram-dm') return 'image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif';
    return 'image/jpeg,image/png,.jpg,.jpeg,.png';
  }
  return '';
}

export interface WaMediaErrorBody {
  message?: string;
  code?: string;
}

export interface WaMediaMessageResponse {
  message_id?: string | null;
  track_id?: string | null;
  status?: string;
  error?: WaMediaErrorBody | string | null;
  media_url?: string;
  [key: string]: unknown;
}

export function plainMediaErrorText(error: WaMediaErrorBody | string | null | undefined): string {
  if (typeof error === 'string') return error.trim();
  if (error && typeof error.message === 'string') return error.message.trim();
  return '';
}

/** Prefer error.message, then the same top-level message/detail fields used by text sends. */
export function mediaResponseErrorText(result: WaMediaMessageResponse): string {
  const fromError = plainMediaErrorText(result.error);
  if (fromError) return fromError;
  if (typeof result.message === 'string' && result.message.trim()) return result.message.trim();
  const { detail } = result;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  return '';
}

export function plainMediaErrorCode(
  error: WaMediaErrorBody | string | null | undefined,
): string | undefined {
  if (error && typeof error === 'object' && typeof error.code === 'string' && error.code) {
    return error.code;
  }
  return undefined;
}

export interface MediaSendFailure {
  message: string;
  code?: string;
  promptRecharge: boolean;
}

const WINDOW_CLOSED_MESSAGE = 'Han pasado más de 24 horas desde la última respuesta del usuario. '
  + 'Solo se pueden enviar plantillas aprobadas.';

/** Maps media_message error codes to plain text. UNSUPPORTED_MEDIA keeps the server text. */
export function resolveMediaSendFailure(
  code: string | undefined,
  serverMessage: string | undefined,
): MediaSendFailure {
  const plain = (serverMessage || '').trim();
  switch (code) {
    case 'UNSUPPORTED_MEDIA':
      return {
        message: plain || 'El archivo no es válido para WhatsApp.',
        code,
        promptRecharge: false,
      };
    case 'VALIDATION_ERROR':
      return { message: 'Archivo inválido.', code, promptRecharge: false };
    case 'RESOURCE_NOT_FOUND':
      return { message: 'Recurso no encontrado.', code, promptRecharge: false };
    case 'WHATSAPP_MESSAGING_WINDOW_ERROR':
      return { message: WINDOW_CLOSED_MESSAGE, code, promptRecharge: false };
    case 'INSUFFICIENT_CREDITS':
      return {
        message: 'No tienes créditos suficientes para enviar este mensaje.',
        code,
        promptRecharge: true,
      };
    case 'MESSAGE_PROCESSING_ERROR':
      return {
        message: 'No se pudo enviar el mensaje. Intenta de nuevo.',
        code,
        promptRecharge: false,
      };
    default:
      return {
        message: plain || 'Error enviando mensaje',
        code,
        promptRecharge: false,
      };
  }
}

export function stickerMediaUrl(metadata: {
  sticker?: { media_url?: string; url?: string };
} | null | undefined): string {
  const sticker = metadata?.sticker;
  if (!sticker) return '';
  if (typeof sticker.media_url === 'string' && sticker.media_url) return sticker.media_url;
  if (typeof sticker.url === 'string' && sticker.url) return sticker.url;
  return '';
}
